import { Modal, PixelIcon } from '@/components/ui';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { getMenuHapticPattern } from '@/utils/haptics';
import React, { useEffect, useState } from 'react';

export interface LevelCompleteStats {
  stepsTaken: number;
  timeSpent: number;
  retries: number;
  keyPressSpeed: number;
  stars: number;
  score?: number;
  collectiblesCollected?: number;
  collectiblesTotal?: number;
}

export interface LevelCompleteModalProps {
  isOpen: boolean;
  levelWon: boolean;
  title: string;
  subtitle: string;
  stats: LevelCompleteStats;
  autoCloseDuration?: number;
  onContinue: () => void;
  onRetry: () => void;
  onClose: () => void;
}

const LevelCompleteModal: React.FC<LevelCompleteModalProps> = ({
  isOpen,
  levelWon,
  title,
  subtitle,
  stats,
  autoCloseDuration = 10,
  onContinue,
  onRetry,
  onClose,
}) => {
  const [countdown, setCountdown] = useState(autoCloseDuration);
  const { pattern } = useHapticFeedback();
  const [hasTriggeredOpenFeedback, setHasTriggeredOpenFeedback] = useState(false);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(autoCloseDuration);
    }
  }, [isOpen, autoCloseDuration]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handlePrimaryAction();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Delight haptics for completion/failure panel entry.
  useEffect(() => {
    if (!isOpen) {
      setHasTriggeredOpenFeedback(false);
      return;
    }
    if (hasTriggeredOpenFeedback) return;

    const nextPattern = levelWon
      ? getMenuHapticPattern('levelComplete')
      : getMenuHapticPattern('warning');
    pattern(nextPattern);
    setHasTriggeredOpenFeedback(true);
  }, [hasTriggeredOpenFeedback, isOpen, levelWon, pattern]);

  // Keyboard handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handlePrimaryAction();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, levelWon]);

  const handlePrimaryAction = () => {
    if (levelWon) {
      onContinue();
    } else {
      onRetry();
    }
  };

  const getCountdownClass = () => {
    const percentage = countdown / autoCloseDuration;
    if (percentage <= 0.3) return 'urgent';
    if (percentage <= 0.6) return 'warning';
    return '';
  };

  // Icon components
  const SuccessIcon = () => <PixelIcon name="success" size={48} className="w-12 h-12" />;
  const RetryIcon = () => <PixelIcon name="retry" size={48} className="w-12 h-12" />;
  const StarIcon = ({ filled }: { filled: boolean }) => (
    <PixelIcon name="star" size={32} className={`w-8 h-8 ${filled ? 'text-yellow-400' : 'text-gray-500'}`} />
  );

  // Stat icons
  const StatIcons = {
    stepsTaken: <PixelIcon name="steps" size={20} className="w-5 h-5" />,
    timeSpent: <PixelIcon name="clock" size={20} className="w-5 h-5" />,
    retries: <PixelIcon name="retry" size={20} className="w-5 h-5" />,
    keyPressSpeed: <PixelIcon name="speed" size={20} className="w-5 h-5" />,
  };

  const modalContent = (
    <div className="level-complete-modal menu-delight-panel menu-delight-panel--success">
      <div className="menu-delight-panel__splashes" aria-hidden="true">
        <span className="menu-delight-panel__splash menu-delight-panel__splash--1" />
        <span className="menu-delight-panel__splash menu-delight-panel__splash--2" />
        <span className="menu-delight-panel__splash menu-delight-panel__splash--3" />
      </div>

      {/* Header */}
      <div className={`level-complete-header ${levelWon ? 'success' : 'retry'}`}>
        <div className="level-complete-icon menu-pop">{levelWon ? <SuccessIcon /> : <RetryIcon />}</div>
        <h1 className="level-complete-title">{title}</h1>
      </div>

      {/* Subtitle */}
      <h2 className="level-complete-subtitle">{subtitle}</h2>

      {/* Stars */}
      {stats.stars !== undefined && (
        <div className="level-complete-stars">
          {[1, 2, 3].map(star => (
            <div
              key={star}
              className={`star-wrapper ${star <= stats.stars ? 'earned' : 'unearned'}`}
              style={{ animationDelay: `${(star - 1) * 0.2}s` }}
            >
              <StarIcon filled={star <= stats.stars} />
            </div>
          ))}
        </div>
      )}

      {/* Statistics */}
      <div className="level-complete-stats">
        {Object.entries({
          stepsTaken: { label: 'Steps', icon: StatIcons.stepsTaken },
          timeSpent: { label: 'Time', icon: StatIcons.timeSpent },
          retries: { label: 'Retries', icon: StatIcons.retries },
          keyPressSpeed: { label: 'Speed', icon: StatIcons.keyPressSpeed },
        }).map(([key, config]) => {
          const value = stats[key as keyof LevelCompleteStats];
          if (value === undefined || (value === 0 && key !== 'retries')) return null;

          return (
            <div key={key} className="stat-item">
              <div className="stat-icon">{config.icon}</div>
              <div className="stat-content">
                <span className="stat-label">{config.label}</span>
                <span className="stat-value">{value}</span>
              </div>
            </div>
          );
        })}
        {/* Collectibles ratio */}
        {stats.collectiblesTotal !== undefined && stats.collectiblesTotal > 0 && (
          <div className="stat-item">
            <div className="stat-icon">
              <PixelIcon name="gem" size={20} className="w-5 h-5" />
            </div>
            <div className="stat-content">
              <span className="stat-label">Collectibles</span>
              <span className="stat-value">
                {stats.collectiblesCollected ?? 0}/{stats.collectiblesTotal}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="level-complete-actions">
        <button
          className={`continue-button ${levelWon ? 'btn-success' : 'btn-warning'}`}
          onClick={handlePrimaryAction}
        >
          <span className="btn-icon">
            {levelWon ? <PixelIcon name="play" size={16} className="w-4 h-4" /> : <PixelIcon name="retry" size={16} className="w-4 h-4" />}
          </span>
          <span className="btn-text">{levelWon ? 'Continue' : 'Retry'}</span>
        </button>

        {/* Add Retry button for successful levels */}
        {levelWon && (
          <button className="retry-button btn-secondary" onClick={onRetry}>
            <span className="btn-icon">
              <PixelIcon name="retry" size={16} className="w-4 h-4" />
            </span>
            <span className="btn-text">Retry</span>
          </button>
        )}
      </div>

      {/* Countdown */}
      <div className="level-complete-countdown" aria-live="polite">
        <span className="countdown-label">Auto-continue in:</span>
        <span key={countdown} className={`countdown-timer countdown-timer--pop ${getCountdownClass()}`}>
          {countdown}
        </span>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant={levelWon ? 'success' : 'warning'}
      size="medium"
      showCloseButton={false}
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      {modalContent}
    </Modal>
  );
};

export default LevelCompleteModal;
